// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/calculation.ts
import { PAYROLL_CATALOG_VARIABLES } from './catalog';
import { evaluatePayrollFormulaSet, extractPayrollDependencies } from './formulaEvaluator';
import { PAYROLL_SEMANTIC_CODES, canonicalPayrollCode } from './domain';
import { PAYROLL_VARIABLE_SOURCE_REGISTRY, resolvePayrollSourceValues } from './sourceRegistry';

export function roundVnd(value) { return Math.round(Number(value) || 0); }

// Tiền làm tròn về đồng, nhưng số và tỷ lệ giữ phần lẻ (giờ tăng ca 6,25 lưu thành 6 thì phiếu lương
// in sai giờ). Cắt ở 4 chữ số để không ra đuôi 0,30000000000000004. Không rõ kiểu thì làm tròn như tiền.
export function roundByDataType(value, dataType) {
    if (dataType === 'SO' || dataType === 'TY_LE') return Math.round((Number(value) || 0) * 1e4) / 1e4;
    return roundVnd(value);
}

function isRecipientEffective(recipient, date) {
    const time = date.getTime();
    return (!recipient.effectiveFrom || recipient.effectiveFrom.getTime() <= time)
        && (!recipient.effectiveTo || recipient.effectiveTo.getTime() >= time);
}

// Chia tiền theo trọng số mà tổng các phần vẫn bằng đúng tổng ban đầu đến từng đồng (phần dư chia
// cho người có phần lẻ lớn nhất).
function allocateRoundedAmount(total, weights) {
    if (!weights.length) return [];
    const sign = total < 0 ? -1 : 1;
    const absoluteTotal = Math.abs(roundVnd(total));
    const weightTotal = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
    if (weightTotal <= 0) return weights.map(() => 0);
    const raw = weights.map((weight) => absoluteTotal * Math.max(0, weight) / weightTotal);
    const allocated = raw.map(Math.floor);
    const remaining = absoluteTotal - allocated.reduce((sum, value) => sum + value, 0);
    const order = raw
        .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
        .sort((left, right) => right.remainder - left.remainder || left.index - right.index);
    for (let index = 0; index < remaining; index += 1) allocated[order[index % order.length].index] += 1;
    return allocated.map((value) => value * sign);
}

/**
 * Đổi dòng năng suất (theo tổ–công đoạn–ngày) thành dòng lương theo người nhận.
 * recipients: [{ id, allocationId, employeeId, coefficient, effectiveFrom, effectiveTo }]; không có
 * người nhận theo hệ số thì dùng row.employeeId (người phụ trách) với hệ số 1.
 */
export function calculateProductivityLines(rows, recipients = []) {
    const recipientsByAllocation = new Map();
    for (const recipient of recipients) {
        recipientsByAllocation.set(recipient.allocationId, [
            ...(recipientsByAllocation.get(recipient.allocationId) ?? []),
            recipient,
        ]);
    }
    const lines = [];
    const unmatchedRecords = [];
    for (const row of rows) {
        const date = new Date(row.ngay);
        const activeRecipients = row.allocationId
            ? (recipientsByAllocation.get(row.allocationId) ?? []).filter((recipient) => isRecipientEffective(recipient, date) && recipient.coefficient > 0)
            : [];
        const resolvedRecipients = activeRecipients.length
            ? activeRecipients
            : row.employeeId
                ? [{ id: '', allocationId: row.allocationId ?? '', employeeId: row.employeeId, coefficient: 1 }]
                : [];
        if (!resolvedRecipients.length) {
            unmatchedRecords.push(row);
            continue;
        }
        const totalCoefficient = resolvedRecipients.reduce((sum, recipient) => sum + recipient.coefficient, 0);
        const amounts = allocateRoundedAmount(row.thanhTien, resolvedRecipients.map((recipient) => recipient.coefficient));
        resolvedRecipients.forEach((recipient, index) => {
            const ratio = recipient.coefficient / totalCoefficient;
            const adjustment = row.tienHieuChinh ?? 0;
            lines.push({
                NHAN_SU_ID: recipient.employeeId,
                TO_PBNS_ID: row.allocationId,
                TO: row.to ?? '',
                PHAN_BO_NS_ID: recipient.id || null,
                MA_CONG_DOAN: row.maCongDoan,
                TEN_CONG_DOAN: row.congDoan,
                NGAY: date,
                KHOI_LUONG: Number((row.khoiLuong * ratio).toFixed(6)),
                KHOI_LUONG_NGUON: row.khoiLuong,
                DON_VI_TINH: row.donViTinh,
                DON_GIA: row.donGia - adjustment,
                TIEN_HIEU_CHINH: adjustment,
                THANH_TIEN: amounts[index],
                THANH_TIEN_NGUON: roundVnd(row.thanhTien),
                HE_SO: recipient.coefficient,
                TY_LE_PHAN_BO: ratio,
                SOURCE_MODEL: row.source,
                SOURCE_ID: row.id,
            });
        });
    }
    return { lines, unmatchedRecords };
}

// Gộp dòng năng suất theo đơn vị tính: khối lượng khác đơn vị không cộng được, tiền thì cộng được.
// Tổng thanhTien của mọi nhóm luôn bằng LSP.
export function summarizePayrollLinesByUnit(lines) {
    const groups = new Map();
    for (const line of lines) {
        const donViTinh = line.DON_VI_TINH?.trim() || 'Khác';
        const group = groups.get(donViTinh) ?? { donViTinh, khoiLuong: 0, thanhTien: 0 };
        group.khoiLuong += line.KHOI_LUONG;
        group.thanhTien += line.THANH_TIEN;
        groups.set(donViTinh, group);
    }
    return Array.from(groups.values())
        .map((group) => ({ ...group, khoiLuong: Number(group.khoiLuong.toFixed(6)), thanhTien: roundVnd(group.thanhTien) }))
        .sort((left, right) => right.thanhTien - left.thanhTien || left.donViTinh.localeCompare(right.donViTinh, 'vi'));
}

// Bộ công thức mặc định suy thẳng từ danh mục, không chép tay để hai nơi không lệch nhau.
const DEFAULT_FORMULAS = Object.fromEntries(
    PAYROLL_CATALOG_VARIABLES.flatMap((variable) => (variable.formula ? [[variable.code, variable.formula]] : [])),
);

function normalizeValues(values = {}) {
    return Object.fromEntries(Object.entries(values).map(([code, value]) => [canonicalPayrollCode(code), Number(value) || 0]));
}

function valueOf(values, key) { return Number(values[canonicalPayrollCode(key)] ?? values[key] ?? 0); }

/**
 * Tính công thức hai lượt: lượt gốc chưa ghi đè, lượt hiện tại có ghi đè. Biến bị ghi đè dùng số ghi
 * đè và bỏ công thức của nó, các biến phụ thuộc tính lại theo số đó.
 * overrides: { [code]: { value, reason, by, at } }
 */
export function evaluatePayrollVariables(input) {
    const formulas = input.formulas === undefined ? DEFAULT_FORMULAS : input.formulas;
    const original = evaluatePayrollFormulaSet(formulas, normalizeValues(input.baseValues)).values;
    const overrideEntries = Object.entries(input.overrides ?? {}).map(([code, override]) => [canonicalPayrollCode(code), override]);
    for (const [code] of overrideEntries) if (!(code in original)) throw new Error(`Không thể ghi đè biến không tồn tại: ${code}`);
    const currentBase = { ...normalizeValues(input.baseValues), ...Object.fromEntries(overrideEntries.map(([code, override]) => [code, override.value])) };
    const currentFormulas = Object.fromEntries(Object.entries(formulas).filter(([code]) => !overrideEntries.some(([overrideCode]) => overrideCode === canonicalPayrollCode(code))));
    const values = evaluatePayrollFormulaSet(currentFormulas, currentBase).values;
    const variables = Object.keys(original).map((code) => ({
        code,
        originalValue: roundByDataType(original[code], input.dataTypes?.[code]),
        currentValue: roundByDataType(values[code], input.dataTypes?.[code]),
    }));
    for (const [code, override] of overrideEntries) {
        const item = variables.find((candidate) => candidate.code === code);
        if (item) {
            item.overridden = true;
            item.overrideReason = override.reason;
            item.overrideBy = override.by ?? null;
            item.overrideAt = override.at ?? null;
        }
    }
    return { values, variables };
}

/**
 * Tính lương cả kỳ.
 * input.employees: [{ employeeId, soHd, hoTen, to, agreement, attendance, inputValues, formulas,
 *   productivityLines, templateId, advances, overrides, activeCodes, agreementCodes, variableSources }]
 * input.variableDataTypes: { [code]: 'SO' | 'TIEN' | 'TY_LE' | 'TEXT' }
 */
export function calculatePayrollPeriod(input) {
    const warnings = [];
    const employees = input.employees.map((employee) => {
        const agreement = employee.agreement;
        const attendance = employee.attendance;
        const manual = normalizeValues(employee.inputValues ?? {});
        const agreementValues = normalizeValues(agreement?.values ?? {});
        const workDays = manual.NC ?? attendance?.workDays ?? 0;
        const activeCodes = employee.activeCodes?.map(canonicalPayrollCode)
            ?? Array.from(new Set([...Object.keys(PAYROLL_VARIABLE_SOURCE_REGISTRY), ...Object.keys(employee.formulas ?? input.formulas ?? DEFAULT_FORMULAS)]));
        const employeeWarnings = [];
        const requiresAgreement = (employee.agreementCodes?.length ?? 0) > 0;
        if (!agreement && requiresAgreement) {
            // Không chặn cả kỳ vì vài người thiếu thỏa thuận: người thiếu ra 0, người đủ vẫn xem được
            // lương ngay. Chốt chặn nằm ở bước gửi duyệt, phải tick xác nhận mọi cảnh báo.
            employeeWarnings.push({ code: 'MISSING_AGREEMENT', message: `Nhân sự ${employee.soHd} chưa có thỏa thuận lương hiệu lực nên các khoản theo thỏa thuận tính bằng 0.`, blocking: false, employeeId: employee.employeeId });
        }
        const sourceResolution = resolvePayrollSourceValues(activeCodes, {
            manualValues: manual,
            agreementValues,
            // Hằng số công ty được đưa vào inputValues (manual) ở bước dựng đầu vào nên đọc lại ở đây.
            systemValues: manual,
            attendance: attendance ? { ...attendance, workDays } : undefined,
            productivityLines: employee.productivityLines,
            advances: employee.advances,
        }, employee.variableSources);
        const baseValues = { ...sourceResolution.values };
        const usesMonthlySalary = activeCodes.includes('LTH');
        if (usesMonthlySalary) {
            if ((baseValues.MLT ?? 0) <= 0) {
                employeeWarnings.push({ code: 'INVALID_MONTHLY_SALARY', message: `Nhân sự ${employee.soHd} thiếu mức lương tháng (MLT) hợp lệ.`, blocking: true, employeeId: employee.employeeId, variableCode: 'LTH' });
            }
            if ((baseValues.NCC ?? 0) <= 0) {
                employeeWarnings.push({ code: 'INVALID_STANDARD_WORK_DAYS', message: `Nhân sự ${employee.soHd} thiếu ngày công chuẩn (NCC) lớn hơn 0.`, blocking: true, employeeId: employee.employeeId, variableCode: 'LTH' });
                // Giữ được bước xem trước để báo lỗi nghiệp vụ thay vì dừng cả kỳ vì chia cho 0.
                baseValues.NCC = 1;
                baseValues.MLT = 0;
            }
        }
        const evaluated = evaluatePayrollVariables({ baseValues, formulas: employee.formulas ?? input.formulas, overrides: employee.overrides, dataTypes: input.variableDataTypes });
        const values = evaluated.values;
        if (usesMonthlySalary && sourceResolution.values.NCC > 0 && valueOf(values, 'NCT') > sourceResolution.values.NCC) {
            employeeWarnings.push({ code: 'PAID_DAYS_EXCEED_STANDARD', message: `Nhân sự ${employee.soHd} có ngày hưởng lương (NCT) lớn hơn ngày công chuẩn (NCC).`, blocking: false, employeeId: employee.employeeId, variableCode: 'LTH' });
        }
        const traceMap = new Map(sourceResolution.traces.map((trace) => [trace.code, trace]));
        for (const [code, formula] of Object.entries(employee.formulas ?? input.formulas ?? DEFAULT_FORMULAS)) {
            const canonicalCode = canonicalPayrollCode(code);
            const variable = evaluated.variables.find((item) => canonicalPayrollCode(item.code) === canonicalCode);
            traceMap.set(canonicalCode, {
                code: canonicalCode,
                source: 'CONG_THUC',
                sourceValue: variable?.originalValue ?? 0,
                formula,
                dependencies: Object.fromEntries(extractPayrollDependencies(formula).map((dependency) => [dependency, values[dependency] ?? 0])),
                calculatedValue: variable?.currentValue ?? 0,
                overriddenValue: variable?.overridden ? variable.currentValue : undefined,
            });
        }
        const result = {
            employeeId: employee.employeeId,
            soHd: employee.soHd,
            hoTen: employee.hoTen,
            to: employee.to ?? null,
            variables: evaluated.variables,
            values,
            productivityLines: employee.productivityLines,
            templateId: employee.templateId ?? null,
            totalGross: roundVnd(valueOf(values, PAYROLL_SEMANTIC_CODES.gross)),
            advanceDeduction: roundVnd(valueOf(values, PAYROLL_SEMANTIC_CODES.advance)),
            insurance: roundVnd(valueOf(values, PAYROLL_SEMANTIC_CODES.employeeInsurance)),
            netPay: roundVnd(valueOf(values, PAYROLL_SEMANTIC_CODES.net)),
            warnings: employeeWarnings,
            traces: activeCodes.flatMap((code) => traceMap.get(code) ?? []),
        };
        warnings.push(...result.warnings);
        return result;
    });

    return {
        employees,
        summary: {
            totalGross: roundVnd(employees.reduce((sum, employee) => sum + employee.totalGross, 0)),
            totalAdvance: roundVnd(employees.reduce((sum, employee) => sum + employee.advanceDeduction, 0)),
            totalInsurance: roundVnd(employees.reduce((sum, employee) => sum + employee.insurance, 0)),
            totalNet: roundVnd(employees.reduce((sum, employee) => sum + employee.netPay, 0)),
        },
        warnings,
        unmatchedRecords: [],
    };
}
